import React, { useState, useEffect } from 'react'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge'
import defaultTexts from '../../../texts/texts'
import { capitalizeFirstLetter } from '../../_common/_helpers/string'
import { getTextedDate, getDifferenceInDays, getDaysInMonth, getDaysInYear } from '../_helpers/dates'
import { sortByPriority } from '../_helpers/sort'

const SubscriptionItem = (props) => {
  const { groupName, title, services, fee, currency, duration, status, created_at, end_time } = props
  const theme = useTheme()

  const [servicesData, setServicesData] = useState([])
  const [differenceInDays, setDifferenceInDays] = useState(getDifferenceInDays(null, end_time))

  const gaugeSum = duration === 'month' ? getDaysInMonth() : getDaysInYear()

  const wrapperStyle = {
    background: theme.palette.aliceBlue,
    borderRadius: '50px 20px 20px 20px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    marginBottom: '40px',
    minHeight: '550px',
  }

  const labelStyle = {
    height: '60px',
    background: theme.palette.oxfordBlue,
    color: 'white',
    fontSize: '20px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 40px',    
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const bodyStyle = {
    minHeight: '450px',
    padding: '20px',
  }

  const servicesLabelStyle = {
    fontSize: '20px',
    fontWeight: '500',
    color: theme.palette.oxfordBlue,
    padding: '20px 0px',
  }

  const serviceBulletStyle = {
    background: theme.palette.violetsAreBlue,
    width: '10px',
    height: '10px',
    borderRadius: '5px',
  }

  const serviceTitleStyle = {
    fontSize: '18px',
    fontWeight: '500',
    color: theme.palette.oxfordBlue,
  }

  const gaugeValueStyle = {
    width: '200px',
    fontSize: '36px',
    lineHeight: '32px',
    fontWeight: '500',
    color: theme.palette.violetsAreBlue,
    position: 'absolute',
    top: '55px',
    left: '0px',
    textAlign: 'center',
  }

  const monthlyPriceStyle = {
    color: theme.palette.violetsAreBlue,
    fontSize: '24px',
    fontWeight: '500',
  }

  const paramKeyStyle = {
    color: theme.palette.grayX11,
    fontSize: '18px',
    fontWeight: '500',
  }

  const paramValueStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '18px',
    fontWeight: '500',
  }

  const paramBigValueStyle = {
    fontSize: '24px',
    fontWeight: '500',
  }

  useEffect(() => {
    if (services && services.length > 0) {
      setServicesData(sortByPriority(services))
    } else {
      setServicesData([])
    }
  }, [services])

  useEffect(() => {
    setDifferenceInDays(getDifferenceInDays(null, end_time))
  }, [end_time])

  return (
    <Box sx={wrapperStyle}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='flex-start'
      >
        <Box sx={labelStyle}>{groupName} {defaultTexts.subscription}</Box>
      </Stack>            
      <Box sx={bodyStyle}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <Box sx={servicesLabelStyle}>{defaultTexts.productsAndService}</Box>

            {
              servicesData.map((item, index) => (
                <Stack
                  key={index}
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                  spacing={2}
                  sx={{ margin: '8px 0px' }}
                >
                  <Box sx={serviceBulletStyle}></Box>
                  <Box sx={serviceTitleStyle}>{item.title.value}</Box>
                </Stack>
              ))
            }

          </Grid>
          <Grid size={{ xs: 12, lg: 5 }}>
            <Box sx={servicesLabelStyle}>{title.value}</Box>
            
            <Box>
              <Stack
                direction='column'
                alignItems='center'
                justifyContent='flex-start'
                spacing={2}
              >
                <Box sx={{ position: 'relative' }}>
                  <Box sx={gaugeValueStyle}>
                    <Box>{differenceInDays > 0 ? differenceInDays : 0}</Box>
                    <Box sx={{ color: theme.palette.grayX11, fontSize: '24px' }}>{defaultTexts.days}</Box>
                    <Box sx={{ color: theme.palette.grayX11, fontSize: '24px' }}>{defaultTexts.left}</Box>
                  </Box>
                  <Gauge
                    width={200}
                    height={200}
                    value={ differenceInDays > 0 ? (differenceInDays / gaugeSum) * 100 : 0 }
                    cornerRadius='50%'
                    sx={{
                      [`& .${gaugeClasses.valueText}`]: gaugeValueStyle,
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: theme.palette.violetsAreBlue,
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: theme.palette.grayX11,
                      },
                    }}
                    text=''
                  />
                </Box>

                <Box sx={monthlyPriceStyle}>{fee} {currency.toUpperCase()} / {capitalizeFirstLetter(duration)}</Box>

                <Box sx={{ width: '100%' }}>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{ marginBottom: '10px' }}
                  >
                    <Box sx={paramKeyStyle}>{defaultTexts.status}:</Box>
                    <Box
                      sx={{
                        ...paramBigValueStyle,
                        color: status === 'active' ? theme.palette.greenSheen : theme.palette.coralRed,
                      }}
                    >{capitalizeFirstLetter(status)}</Box>
                  </Stack>
                  
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{ marginBottom: '10px' }}
                  >
                    <Box sx={paramKeyStyle}>{defaultTexts.subscribedAt}:</Box>
                    <Box sx={paramValueStyle}>{getTextedDate(created_at)}</Box>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default SubscriptionItem